<template lang="pug">
div
  ValidationProvider(
    v-slot="{ errors }"
    rules="required"
    name="name"
    :model-value="name"
  )
    .seats-increase-warning-box.dialog-warning.blue(v-if="increaseSeatsByPreselected > 0")
      i.d-flex.align-items-center.hi.hi-info-filled.hi-12.text-blue

      div
        | You have {{ preselectedMembers.length }} {{ $filters.pluralize(preselectedMembers.length, 'member') }} who will be added to this project automatically.
        b
          |
          | This will add {{ increaseSeatsByPreselected }} {{ $filters.pluralize(increaseSeatsByPreselected, 'paid seat') }} to your plan
        |
        | because of their roles.
        a(:href="`/organizations/${organization.id}/settings/projects`")
          | View members auto add settings
        |
        | to adjust how you want to add members to new projects.

    #name.form-group.mb-10(:class="{ 'has-error': errors?.[0] }")
      label.control-label(for="name")
        | {{ projectNameLabel }}
        abbr(title="required") *
        i.hi.hi-info-filled.hi-12.a-tooltip.ml-5(
          v-if="isNewForm"
          v-tooltip="{ placement: 'bottom' }"
          title="Create multiple projects by adding project names on separate lines"
        )
      template(v-if="isNewForm")
        textarea.form-control.required(
          v-model="name"
          rows="3"
          placeholder="Add project names separated by new lines"
        )
      template(v-else)
        AppInput(
          v-model="name"
          placeholder="Name"
          :disabled="!canManageDetails"
        )
      AppDialogError(:errors="errors")
  SharedEditFields(
    v-model:billable="billable"
    v-model:monitor-user-activity="disableUserActivity"
    v-model:disable-project-tracking="disableProjectTracking"
    :activity-tracking-allowed="activityTrackingAllowed"
    :can-manage-details="canManageDetails"
    :organization="organization"
    :is-new-form="isNewForm"
  )
  .custom-div
  #disable-client-idle-tracking.form-group.mb-20
    .checkbox
      AppOnOffToggle(
        v-model="disableClientIdleTracking"
        :disabled="!canManageDetails"
      )
        span Disable idle time
          i.hi.hi-info-filled.hi-12.a-tooltip(
            v-tooltip="{ placement: 'bottom' }"
            title="When enabled, this will no longer record idle time for any members tracking time to this project. This is often used on any projects related to calls or meetings"
          )
  #schedule-weekly-report.form-group.mb-20(v-if="showScheduleWeeklyReport")
    .checkbox
      AppOnOffToggle(
        v-model="scheduleWeeklyReport"
        :disabled="!canManageDetails"
      )
        span Schedule weekly report
          i.hi.hi-info-filled.hi-12.a-tooltip(
            v-tooltip="{ placement: 'bottom' }"
            title="You and Project managers will automatically receive a weekly report for this project."
          )

  ValidationProvider(
    v-slot="{ errors }"
    name="Description"
    rules="max:2000"
  )
    #description.form-group(
      v-if="!isNewForm"
      :class="{ 'has-error': errors?.[0] }"
    )
      label.control-label Description
      textarea.form-control.field-description(
        v-model="description"
        rows="4"
        placeholder="Description"
        :disabled="!canManageDetails"
      )
      AppDialogError(:errors="errors")
  ValidationProvider(
    v-slot="{ errors }"
    name="client_id"
    :model-value="clientId"
  )
    #client.form-group(:class="{ 'has-error': errors?.[0] }")
      label.control-label Client
      AppSelect(
        v-model="clientId"
        placeholder="Select client"
        :options="clientOptions"
        :select2-options="select2Options"
        :disabled="!canManageDetails"
      )
      AppDialogError(:errors="errors")
  template(v-if="isNewForm && linkingEnabled")
    div(v-if="linkedOrganization")
      #link-to-tasks.form-group
        .checkbox
          AppCheckbox(
            v-model="linkToTasks"
            :disabled="!canManageDetails"
          )
            | Link to
            img.label-logo(:src="IMAGE_HS_TASKS_LOGO")
            | organization
    div(v-else)
      AppTasksAlert(:organization-id="organization.id")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { mapFormFields } from '~/utils/form_utils';
import { IMAGE_HS_TASKS_LOGO } from '~/rails_assets';
import SharedEditFields from './SharedEditFields.vue';
import { difference, intersection } from 'lodash-es';

export default {
  name: 'ProjectDialogTabGeneral',
  components: { SharedEditFields },
  data: () => ({
    defaultSelect2Options: {
      allowClear: true,
      multiple: false,
      width: '100%',
      language: {
        noResults: () => {
          return 'No clients';
        },
      },
    },
    IMAGE_HS_TASKS_LOGO,
  }),
  computed: {
    ...mapState(['isNewForm', 'organization', 'preselected_members', 'billing_user_ids', 'vacant_seats', 'membersMap']),
    ...mapGetters([
      'clientOptions',
      'linkedOrganization',
      'canManageDetails',
      'linkingEnabled',
      'activityTrackingAllowed',
      'isUserActivityMonitored',
      'showScheduleReport',
    ]),
    ...mapFormFields('project', {
      name: 'name',
      description: 'description',
      billable: 'billable',
      clientId: 'client_id',
      linkToTasks: 'link_to_task',
      disableClientIdleTracking: 'disable_client_idle_tracking',
      disableProjectTracking: 'disable_project_tracking',
      scheduleWeeklyReport: 'schedule_weekly_report',
    }),
    disableUserActivity: {
      get() {
        return !this.isUserActivityMonitored;
      },
      set(value) {
        this.updateField({ path: 'monitor_user_activity', value: !value });
      },
    },
    showScheduleWeeklyReport() {
      return this.showScheduleReport && this.isNewForm;
    },
    select2Options() {
      return { ...this.defaultSelect2Options, ...{ placeholder: 'Select a client' } };
    },
    increaseSeatsByPreselected() {
      if (!this.organization.show_per_seat_ui) {
        return 0;
      }

      const addedByPreselect = difference(this.preselectedMembers, this.billing_user_ids);
      const increaseSeatsMembers = intersection(addedByPreselect, [
        ...this.membersMap.user,
        ...this.membersMap.manager,
      ]);

      return increaseSeatsMembers.length - this.vacant_seats;
    },
    preselectedMembers() {
      return Object.values(this.preselected_members).flat();
    },
    projectNameLabel() {
      return this.isNewForm ? 'Project Names' : 'Project Name';
    },
  },
  methods: {
    ...mapActions(['updateField']),
  },
};
</script>
